import type { FeedbackCategory } from '@/lib/diagnostics/feedbackSubmission';

/**
 * 問題回報的**讀取端**契約(R265;BE `creative-test-v2/docs/94` §5 歷史/詳情 + §9 admin)
 * =============================================================================
 *
 * 送出端(`POST /feedback/reports`,multipart)住在 {@link ../services/http/httpSupportService}
 * 與 {@link ../lib/diagnostics/feedbackSubmission};這一檔只管**回報單送出之後**的
 * 兩個視角:回報者看自己的處理進度、管理員看全站並處置。
 *
 * ## 三層 DTO 是繼承關係(BE schema 就是這樣長的)
 *
 * ```
 * ReportListItem      ← GET /reports            (清單;**沒有** signed URL)
 *   └─ ReportDetail   ← GET /reports/{id}       (+ 診斷欄 + 15 分鐘簽名附件)
 *        └─ AdminReportDetail ← GET /feedback/admin/reports(清單,但 `attachments` 被 exclude)
 *                              + PATCH 的回應(完整,含 attachments)
 * ```
 *
 * ⚠️ **admin 清單的 item 是 `AdminReportDetail` 但 `attachments` 被 router 拿掉**
 * (`model_dump(exclude={"attachments"})` —— 20 筆 × 3 張 = 60 次 IAM SignBlob,不值得)。
 * 所以 admin 端要看圖必須另打 `GET /feedback/reports/{id}`(那條對 admin 放行,BE
 * `router.py:325` 的 `is_admin` 分支)。這也是為什麼 `attachments` 在 admin DTO 是選填。
 *
 * ## 為什麼 `status` 是 open string 而不是 union
 *
 * 六值是 BE 的 `Literal`,但 FE 這側**一律原樣保留 wire 值**,顯示 gate 在
 * {@link ../features/feedback/feedbackPresent}。BE 哪天多一個狀態時:契約鎖會紅(強迫
 * 補標籤與色),而在補上之前畫面顯示的是 wire 原值而不是空白 —— R228 那條紅線
 * (「拿 FE union 過濾 wire 值域」= 使用者靜默少看到東西)。
 *
 * ## 文案 SSOT 在後端的那半
 *
 * `resolution_tags` 的 `label` **由 BE 帶在 item 上**(docs/94 §5),FE 原樣顯示、
 * **不進 i18n**。代價明說:en/ja 使用者會看到中文標籤 —— 那是 BE 拍板的架構
 * (改字時只有後端要動、兩邊不會漂),不是漏做翻譯。
 */

// ─── 狀態 ────────────────────────────────────────────────────────────────────

/**
 * BE `FeedbackStatus` 的鏡射(契約鎖雙向相等 —— BE 加狀態 → FE 紅 → 補標籤與色)。
 *
 * 語意(docs/94 §5):`NEW` 待處理 · `TRIAGED` 已確認 · `IN_PROGRESS` 處理中 ·
 * `RESOLVED` 已解決 · `WONT_FIX` 不予處理 · `DUPLICATE` 重複回報。**只有管理員能改**。
 */
export const FEEDBACK_STATUSES = [
  'NEW',
  'TRIAGED',
  'IN_PROGRESS',
  'RESOLVED',
  'WONT_FIX',
  'DUPLICATE',
] as const;

export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];

/** 未結案狀態 —— 與 BE `OPEN_STATUSES` 對齊。⚠️ **只用來標示 chip,不拿來自己算 `open_total`**(那個 BE 給)。 */
export const FEEDBACK_OPEN_STATUSES: readonly FeedbackStatus[] = ['NEW', 'TRIAGED', 'IN_PROGRESS'];

/** 單筆最多幾個罐頭標籤(BE `MAX_RESOLUTION_TAGS`;超過 = 422)。 */
export const FEEDBACK_MAX_RESOLUTION_TAGS = 3;

/** 公開處理說明上限(BE `resolution_note_public` max_length)。 */
export const FEEDBACK_NOTE_PUBLIC_MAX = 500;

/** 內部註記上限(BE `internal_note` max_length)。 */
export const FEEDBACK_INTERNAL_NOTE_MAX = 2000;

/** 清單分頁上限(BE `Query(le=50)`;超過是 FastAPI 422,拿不到業務碼)。 */
export const FEEDBACK_LIST_LIMIT_MAX = 50;

/** 預設每頁筆數(BE 預設值即 20)。 */
export const FEEDBACK_LIST_LIMIT_DEFAULT = 20;

// ─── wire DTO(snake_case)────────────────────────────────────────────────────

export interface FeedbackResolutionTagDto {
  code: string;
  label: string;
}

export interface FeedbackAttachmentDto {
  index: number;
  width: number;
  height: number;
  size_bytes: number;
  download_url: string;
  download_url_expires_datetime: string;
}

/** `GET /feedback/reports` 的 item(BE `ReportListItem`)。 */
export interface FeedbackReportItemDto {
  report_id: string;
  serial: string;
  category: string;
  title: string;
  description: string;
  status: string;
  attachment_count: number;
  created_datetime: string | null;
  updated_datetime: string | null;
  resolution_note_public?: string | null;
  resolution_tags?: FeedbackResolutionTagDto[] | null;
  status_updated_datetime?: string | null;
}

/** `GET /feedback/reports/{id}`(BE `ReportDetail`)。 */
export interface FeedbackReportDetailDto extends FeedbackReportItemDto {
  page_url?: string | null;
  app_version?: string | null;
  viewport?: string | null;
  occurred_at?: string | null;
  project_id?: string | null;
  job_id?: string | null;
  node_id?: string | null;
  attachments?: FeedbackAttachmentDto[] | null;
  attachment_failed_count?: number | null;
}

/**
 * admin 視角(BE `AdminReportDetail`)。
 *
 * ⚠️ 清單那條的 `attachments` 被 router `exclude` 掉 → 這裡是選填,**不可以**假設有值。
 */
export interface FeedbackAdminItemDto extends FeedbackReportDetailDto {
  reporter_uid?: string | null;
  reporter_email?: string | null;
  reporter_display_name?: string | null;
  group_id?: string | null;
  user_agent?: string | null;
  internal_note?: string | null;
  logfire_trace_id?: string | null;
  client_report_id?: string | null;
  route?: string | null;
  diagnostics_json?: string | null;
  status_updated_by_uid?: string | null;
}

export interface FeedbackSummaryDto {
  counts?: Record<string, number> | null;
  total?: number | null;
  open_total?: number | null;
}

export interface FeedbackListResponseDto {
  items?: FeedbackReportItemDto[] | null;
  next_cursor?: string | null;
}

export interface FeedbackAdminListResponseDto {
  items?: FeedbackAdminItemDto[] | null;
  next_cursor?: string | null;
}

export interface FeedbackResolutionTagsResponseDto {
  items?: FeedbackResolutionTagDto[] | null;
}

// ─── DTO 鍵集(契約鎖用;tsc 強制窮舉)────────────────────────────────────────
//
// 慣例對齊 `LIBRARY_ITEM_DTO_KEYS` / `SYNC_JOB_DETAIL_DTO_KEYS`:DTO 加了欄位卻忘了
// 補進這份清單 → 下面那行 tsc 紅。契約鎖再拿這份清單對 BE model 欄集比「BE ⊆ FE」。
// R219/R236 的教訓:少了這一層,BE 加欄位是**零徵兆**的靜默漏接。

export const FEEDBACK_ITEM_DTO_KEYS = [
  'report_id', 'serial', 'category', 'title', 'description', 'status',
  'attachment_count', 'created_datetime', 'updated_datetime',
  'resolution_note_public', 'resolution_tags', 'status_updated_datetime',
] as const;

type _MissingItemKey = Exclude<keyof FeedbackReportItemDto, (typeof FEEDBACK_ITEM_DTO_KEYS)[number]>;
const _itemKeysExhaustive: _MissingItemKey extends never ? true : never = true;
void _itemKeysExhaustive;

export const FEEDBACK_DETAIL_DTO_KEYS = [
  ...FEEDBACK_ITEM_DTO_KEYS,
  'page_url', 'app_version', 'viewport', 'occurred_at',
  'project_id', 'job_id', 'node_id',
  'attachments', 'attachment_failed_count',
] as const;

type _MissingDetailKey = Exclude<
  keyof FeedbackReportDetailDto,
  (typeof FEEDBACK_DETAIL_DTO_KEYS)[number]
>;
const _detailKeysExhaustive: _MissingDetailKey extends never ? true : never = true;
void _detailKeysExhaustive;

export const FEEDBACK_ADMIN_ITEM_DTO_KEYS = [
  ...FEEDBACK_DETAIL_DTO_KEYS,
  'reporter_uid', 'reporter_email', 'reporter_display_name', 'group_id',
  'user_agent', 'internal_note', 'logfire_trace_id', 'client_report_id',
  'route', 'diagnostics_json', 'status_updated_by_uid',
] as const;

type _MissingAdminKey = Exclude<
  keyof FeedbackAdminItemDto,
  (typeof FEEDBACK_ADMIN_ITEM_DTO_KEYS)[number]
>;
const _adminKeysExhaustive: _MissingAdminKey extends never ? true : never = true;
void _adminKeysExhaustive;

export const FEEDBACK_ATTACHMENT_DTO_KEYS = [
  'index', 'width', 'height', 'size_bytes', 'download_url', 'download_url_expires_datetime',
] as const;

type _MissingAttachmentKey = Exclude<
  keyof FeedbackAttachmentDto,
  (typeof FEEDBACK_ATTACHMENT_DTO_KEYS)[number]
>;
const _attachmentKeysExhaustive: _MissingAttachmentKey extends never ? true : never = true;
void _attachmentKeysExhaustive;

// ─── domain(camelCase)──────────────────────────────────────────────────────

export interface FeedbackResolutionTag {
  code: string;
  /** BE 帶來的顯示文案 —— **原樣顯示,不翻譯**(文案 SSOT 在後端,見檔頭)。 */
  label: string;
}

export interface FeedbackAttachment {
  index: number;
  width: number;
  height: number;
  sizeBytes: number;
  /**
   * ⚠️ **15 分鐘 TTL,不是永久網址**(docs/94 §8 驗收項 5):絕不落 localStorage、
   * 絕不分享 —— 這個 URL 本身就是取圖憑證。每次開詳情現簽現用。
   */
  downloadUrl: string;
  expiresAt: string | null;
}

/** 回報者視角的清單 item。 */
export interface FeedbackReportItem {
  reportId: string;
  /** `FB-20260819-0002` —— 人可讀編號,使用者拿它跟我們對話。 */
  serial: string;
  category: FeedbackCategory | string;
  title: string;
  /** ⚠️ 允許 HTML 片段與 stack trace(BE 刻意不擋)→ **一律 React 文字節點輸出**。 */
  description: string;
  /** wire 原值(open string;顯示 gate 在 `feedbackPresent.statusMeta`)。 */
  status: string;
  attachmentCount: number;
  createdAt: string | null;
  updatedAt: string | null;
  /** admin 的公開處理說明;`null` = 還沒寫(不是空字串)。 */
  resolutionNotePublic: string | null;
  resolutionTags: FeedbackResolutionTag[];
  statusUpdatedAt: string | null;
}

export interface FeedbackReportDetail extends FeedbackReportItem {
  pageUrl: string | null;
  appVersion: string | null;
  viewport: string | null;
  occurredAt: string | null;
  projectId: string | null;
  jobId: string | null;
  nodeId: string | null;
  attachments: FeedbackAttachment[];
  attachmentFailedCount: number;
}

export interface FeedbackAdminItem extends FeedbackReportDetail {
  reporterUid: string | null;
  reporterEmail: string | null;
  reporterDisplayName: string | null;
  groupId: string | null;
  userAgent: string | null;
  /** ⚠️ **只有 admin 看得到** —— 回報者的 API 回應永遠不含此欄,FE 也絕不外流到 user 視角。 */
  internalNote: string | null;
  logfireTraceId: string | null;
  clientReportId: string | null;
  route: string | null;
  /** 已去敏的 canonical JSON **字串**;顯示時原文輸出、不解析不渲染。 */
  diagnosticsJson: string | null;
  statusUpdatedByUid: string | null;
}

export interface FeedbackSummary {
  /** 六個狀態一律有 key(BE 保證);未知狀態若出現也原樣收下。 */
  counts: Record<string, number>;
  total: number;
  /** 未結案筆數 —— **BE 算的**,FE 不自己加(日後多一個狀態時 BE 會一起改)。 */
  openTotal: number;
}

export interface FeedbackPage<T> {
  items: T[];
  /** `null` = 沒有下一頁;有值就原封不動帶回 `?cursor=`(**不要解析它**)。 */
  nextCursor: string | null;
}

/**
 * admin 處置 patch —— **dirty-fields-only**。
 *
 * ⚠️ `resolutionTags: undefined`(不給)與 `[]`(清空)是**兩件事**:BE 對前者「不動這欄」、
 * 對後者「清空已選標籤」。所以這個型別刻意用 optional 而不是 nullable,
 * 而 `httpSupportService` 組 body 時只放**真的給了**的鍵(有負向鎖)。
 */
export interface FeedbackAdminPatch {
  status?: string;
  resolutionNotePublic?: string;
  internalNote?: string;
  resolutionTags?: string[];
}

/**
 * patch 欄位 → wire 欄名的**單一來源**。
 *
 * `httpSupportService` 直接迭代這張表組 body,契約鎖再拿 `Object.values` 與 BE 的
 * `AdminPatchRequest.model_fields` **雙向相等**比對。三層綁在一起的效果:
 *   · FE `FeedbackAdminPatch` 加欄位卻沒登記 → `satisfies` 讓 tsc 紅
 *   · BE 加/改/刪欄位 → 契約鎖紅(多送未定義欄位 BE 是 422,整個處置動作會做不了)
 *   · service 忘了送某一欄 → 不可能,它不是逐條 `if` 而是迭代這張表
 */
export const FEEDBACK_PATCH_WIRE_KEYS = {
  status: 'status',
  resolutionNotePublic: 'resolution_note_public',
  internalNote: 'internal_note',
  resolutionTags: 'resolution_tags',
} as const satisfies Record<keyof FeedbackAdminPatch, string>;
